import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { BRAND } from "@/styles/brand";

// ═══════════════════════════════════════════════════════════════════════════
// ADD TO WALLET — Apple Wallet + Google Wallet Buttons
// Generates .pkpass (Apple) or JWT link (Google) from backend
// ═══════════════════════════════════════════════════════════════════════════

// Detect platform
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);

// Apple Wallet Badge SVG
const AppleWalletBadge = () => (
  <svg width="120" height="40" viewBox="0 0 120 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="120" height="40" rx="6" fill="#000"/>
    <text x="60" y="15" textAnchor="middle" fill="#FFF" fontSize="8" fontFamily="sans-serif" fontWeight="400">
      Add to
    </text>
    <text x="60" y="28" textAnchor="middle" fill="#FFF" fontSize="12" fontFamily="sans-serif" fontWeight="600">
      Apple Wallet
    </text>
  </svg>
);

// Google Wallet Badge SVG
const GoogleWalletBadge = () => (
  <svg width="140" height="40" viewBox="0 0 140 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="140" height="40" rx="6" fill="#000"/>
    <text x="70" y="15" textAnchor="middle" fill="#FFF" fontSize="8" fontFamily="sans-serif" fontWeight="400">
      Add to
    </text>
    <text x="70" y="28" textAnchor="middle" fill="#FFF" fontSize="12" fontFamily="sans-serif" fontWeight="600">
      Google Wallet
    </text>
  </svg>
);

export default function AddToWallet({ cardId, compact = false }) {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const handleAppleWallet = async () => {
    if (!user || !token) {
      toast.error("Bitte erst einloggen");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/apple-pass`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { card_id: cardId || user?.member_card || "erdmaennchen" },
        responseType: "blob",
      });

      // Download .pkpass file
      const blob = new Blob([res.data], { type: "application/vnd.apple.pkpass" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "frankysfarm-member.pkpass";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Karte wird zur Wallet hinzugefügt!");
    } catch (e) {
      console.error("Apple Wallet error:", e);
      if (e.response?.status === 501) {
        setShowGuide(true);
      } else {
        toast.error("Wallet-Karte konnte nicht erstellt werden");
      }
    }
    setLoading(false);
  };

  const handleGoogleWallet = async () => {
    if (!user || !token) {
      toast.error("Bitte erst einloggen");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/google-pass`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { card_id: cardId || user?.member_card || "erdmaennchen" },
      });

      if (res.data?.save_url) {
        window.open(res.data.save_url, "_blank");
        toast.success("Karte wird zu Google Wallet hinzugefügt!");
      }
    } catch (e) {
      console.error("Google Wallet error:", e);
      if (e.response?.status === 501) {
        setShowGuide(true);
      } else {
        toast.error("Wallet-Karte konnte nicht erstellt werden");
      }
    }
    setLoading(false);
  };

  if (compact) {
    return (
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={isIOS() ? handleAppleWallet : handleGoogleWallet}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
        style={{
          backgroundColor: "#000",
          color: "#FFF",
          opacity: loading ? 0.6 : 1,
          fontFamily: "'Space Grotesk',sans-serif",
        }}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="4" width="20" height="16" rx="3" stroke="#FFF" strokeWidth="2"/>
            <path d="M2 10h20" stroke="#FFF" strokeWidth="2"/>
            <rect x="5" y="14" width="6" height="2" rx="1" fill="#FFF"/>
          </svg>
        )}
        Zur Wallet
      </motion.button>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 items-center">
        <p className="text-xs text-center mb-1" style={{
          color: BRAND.colors.textMuted,
          fontFamily: "'Figtree',sans-serif",
        }}>
          Karte immer dabei — auf dem Sperrbildschirm
        </p>

        <div className="flex gap-3 justify-center">
          {/* Show platform-specific or both */}
          {(isIOS() || !isAndroid()) && (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleAppleWallet}
              disabled={loading}
              className="relative"
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              <AppleWalletBadge />
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </motion.button>
          )}

          {(isAndroid() || !isIOS()) && (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleGoogleWallet}
              disabled={loading}
              className="relative"
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              <GoogleWalletBadge />
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </motion.button>
          )}
        </div>
      </div>

      {/* Setup Guide Modal (shows when backend returns 501) */}
      <AnimatePresence>
        {showGuide && (
          <motion.div
            className="fixed inset-0 z-[150] flex items-end justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowGuide(false)} />
            <motion.div
              className="relative w-full max-w-md rounded-t-3xl p-6"
              style={{ backgroundColor: "#FFF" }}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25 }}
            >
              <div className="flex justify-center mb-3">
                <div className="w-10 h-1.5 rounded-full bg-gray-200" />
              </div>

              <h3 className="text-lg font-bold mb-2" style={{
                color: BRAND.colors.text,
                fontFamily: "'Space Grotesk',sans-serif",
              }}>
                Wallet wird eingerichtet
              </h3>
              <p className="text-sm mb-4" style={{
                color: BRAND.colors.textMuted,
                fontFamily: "'Figtree',sans-serif",
              }}>
                Die Wallet-Funktion wird gerade von unserem Team aktiviert. 
                Du bekommst eine Push-Benachrichtigung sobald es soweit ist!
              </p>

              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setShowGuide(false);
                  toast.success("Wir benachrichtigen dich!");
                }}
                className="w-full py-4 rounded-2xl font-bold text-white"
                style={{
                  backgroundColor: BRAND.colors.green,
                  fontFamily: "'Space Grotesk',sans-serif",
                }}
              >
                Verstanden
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
